import styled, { css, DefaultTheme } from 'styled-components';
import { TFormVariant } from '../../../form/Form.types';

interface IStateColorsParams {
  error?: boolean;
  borderLess?: boolean;
  disabled?: boolean;
  theme: DefaultTheme;
  variant?: TFormVariant;
  outlined?: boolean;
}

export function getInputStateColors(params: IStateColorsParams) {
  const { theme, borderLess, error, variant } = params;

  let backgroundColor = 'transparent';
  let borderColor = 'transparent';

  const color = variant === 'on-dark' ? theme.color.tint.primaryDark : theme.color.tint.primary;

  if (error) {
    borderColor = variant === 'on-dark' ? theme.color.border.errorDark : theme.color.border.error;
    backgroundColor =
      variant === 'on-dark' ? theme.color.surface.errorDark : theme.color.surface.error;
  } else if (borderLess !== false) {
    borderColor =
      variant === 'on-dark' ? theme.color.border.regularDark : theme.color.border.regular;
    backgroundColor =
      variant === 'on-dark' ? theme.color.surface.variantDark : theme.color.surface.variant;
  }

  return {
    base: {
      color,
      borderColor,
      backgroundColor,
    },
    hover: {
      color: theme.createOverlayColor(color, 'hover'),
      borderColor: theme.createOverlayColor(borderColor, 'hover'),
      backgroundColor: theme.createOverlayColor(backgroundColor, 'hover'),
    },
    focus: {
      color: theme.createOverlayColor(color, 'focused'),
      borderColor: error ? theme.color.solid.error : theme.color.solid.selection,
      backgroundColor,
    },
    disabled: { color, borderColor, backgroundColor },
    readonly: {
      color,
      borderColor,
      backgroundColor: 'transparent',
    },
  };
}

export function getOutlinedInputStateColors(params: Omit<IStateColorsParams, 'borderLess'>) {
  const { theme, error, variant } = params;

  const backgroundColor = 'transparent';
  let borderColor = 'transparent';
  let borderColorFocus = 'transparent';
  const color = variant === 'on-dark' ? theme.color.tint.primaryDark : theme.color.tint.primary;

  if (error) {
    borderColor = variant === 'on-dark' ? theme.color.border.errorDark : theme.color.border.error;
    borderColorFocus =
      variant === 'on-dark' ? theme.color.border.errorDark : theme.color.border.error;
  } else {
    borderColor =
      variant === 'on-dark' ? theme.color.border.emphasisDark : theme.color.border.emphasis;
  }

  return {
    base: {
      color: theme.color.tint.tertiary,
      borderColor,
      backgroundColor,
    },
    hover: {
      color: theme.color.tint.tertiary,
      borderColor: theme.createOverlayColor(
        borderColor,
        'hoverBorder',
        variant === 'on-dark' ? 'dark' : 'light',
      ),
      backgroundColor,
    },
    focus: {
      color: theme.createOverlayColor(
        theme.color.tint.primary,
        'focused',
        variant === 'on-dark' ? 'dark' : 'light',
      ),
      borderColor: theme.createOverlayColor(
        borderColorFocus,
        'hoverBorder',
        variant === 'on-dark' ? 'dark' : 'light',
      ),
      backgroundColor,
    },
    disabled: { color, borderColor, backgroundColor },
    readonly: {
      color,
      borderColor,
      backgroundColor,
    },
  };
}

export const SkeletonSC = styled.div<{
  focused?: boolean;
  error?: boolean;
  readonly?: boolean;
  borderLess?: boolean;
  disabled?: boolean;
  variant?: TFormVariant;
  outlined?: boolean;
}>`
  position: relative;
  display: inline-flex;
  min-height: 40px;
  border-width: 2px;
  border-style: solid;
  border-color: transparent;
  box-sizing: border-box;
  transition: all 0.3s;

  border-radius: ${({ theme }) => theme.radius.small}px;

  input {
    caret-color: ${({ theme }) => theme.color.solid.selection};
  }

  ${(props) => {
    let styling;

    if (props.outlined) {
      styling = getOutlinedInputStateColors(props);
    } else {
      styling = getInputStateColors(props);
    }
    const { base, focus, hover } = styling;

    return css`
      color: ${base.color};
      background-color: ${base.backgroundColor};
      border-color: ${base.borderColor};
      cursor: ${props.readonly ? 'not-allowed' : 'inherit'};

      ${!props.focused && !props.disabled
        ? css`
            :hover {
              color: ${hover.color};
              background-color: ${hover.backgroundColor};
              border-color: ${hover.borderColor};
            }
          `
        : !props.disabled
        ? css`
            color: ${focus.color};
            background-color: ${focus.backgroundColor};
            border-color: ${focus.borderColor};
          `
        : ''}
    `;
  }};

  :placeholder {
    ${({ theme }) => theme.mappedTypography.body2.strong};
    color: ${({ theme }) => theme.color.tint.tertiary};
  }
`;

export const InputContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

export const InputContent = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: row;
  align-items: center;
`;

export const LeftAdornmentSC = styled.div``;

export const RightAdornmentSC = styled.div``;
